import React, {useContext, useMemo, useState} from 'react';
import Accordion from './Filters/Accordion';
import PriceRange from './Filters/PriceRange';
// import RoomCount from './Filters/RoomCount';
import {AppContext} from '../contexts/AppContext';
import {translate} from '../helpers/base';
import {getOptionItems, isGroupSelected} from '../helpers/filters';
import ActiveFilters, {useHasActiveFilters} from './Common/ActiveFilters';
import Pets from './Filters/Pets';
// import AccessibleFacilities from './Filters/AccessibleFacilities';
import SearchField from './Filters/SearchField';
import clsx from 'clsx';

/****************************************************************************************
 * Common helpers
 */

const normalizeQuery = (q) => (q || '').trim().toLowerCase();

const filterOptionsByQuery = (options, query) => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(opt =>
        String(opt.label || '').toLowerCase().includes(q)
    );
};

const getVisibleOptionsData = (filter, rawQuery, expanded) => {
    const {displayedOptionsLimit} = filter;
    const query = normalizeQuery(rawQuery);

    const filteredOptions = filterOptionsByQuery(filter.options || [], query);

    const shouldLimit = filter.options.length > displayedOptionsLimit && !expanded;
    const visibleOptions = shouldLimit ? filteredOptions.slice(0, displayedOptionsLimit) : filteredOptions;
    const hasMoreThanLimit = filteredOptions.length > displayedOptionsLimit;

    return {
        visibleOptions,
        hasMoreThanLimit,
        query,
    };
};

// Count active filters in a group; groups count as 1
const getFilterCount = (selectedByType, filter) => {

    let count = 0;
    const countedGroupIds = new Set();
    const countedSingleKeys = new Set();

    for (const option of filter.options) {
        const isGroup = !!option.isGroup;
        const coveredByGroup = isOptionCoveredBySelectedGroup(option, filter, selectedByType);

        if (isGroup) {
            const groupSelected = isGroupSelected(option, selectedByType);
            const isChecked = groupSelected && !coveredByGroup;

            if (!isChecked) continue;
            if (countedGroupIds.has(option.id)) continue;

            countedGroupIds.add(option.id);
            count++;
        } else {
            const selectedSet = selectedByType[option.type];
            if (!selectedSet || !selectedSet.has(option.id)) continue;
            if (coveredByGroup) continue;

            const key = `${option.type}-${option.id}`;
            if (countedSingleKeys.has(key)) continue;

            countedSingleKeys.add(key);
            count++;
        }
    }

    return count;
};

const isOptionCoveredBySelectedGroup = (option, filter, selectedByType) => {
    if (!filter?.options) return false;

    const itemsOfOption = getOptionItems(option);

    return filter.options.some(groupOption => {
        // Only consider other groups
        if (!groupOption.isGroup || groupOption === option) return false;
        if (!isGroupSelected(groupOption, selectedByType)) return false;

        const groupItems = groupOption.items || [];
        // All items represented by this option must be contained in the group
        return itemsOfOption.every(item =>
            groupItems.some(groupItem => groupItem.type === item.type && groupItem.id === item.id)
        );
    });
};

/****************************************************************************************
 * Component
 */
const Filters = () => {

    const {
        filters,
        selectedFilters,
        subFilters,
        setIsFilterOverlayVisible,
        handleFilterChange,
        handleFilterGroupChange,
        handlePriceRangeChange,
        hasResultsWithPrices,
        isMobile
    } = useContext(AppContext);

    const hasActiveFilters = useHasActiveFilters();

    const [searchQueries, setSearchQueries] = useState({});
    const [expandedFilters, setExpandedFilters] = useState({});

    const excludedGlobalFilterIds = useMemo(() => {
        if (!selectedFilters.accoType || !filters.accommodationType?.options) return new Set();
        const accoTypeOption = filters.accommodationType.options.find(o => o.id === selectedFilters.accoType);
        return new Set(accoTypeOption?.excludedGlobalFilterIds ?? []);
    }, [selectedFilters.accoType, filters.accommodationType]);

    // Normalize selected => { type -> Set(ids) } once per render
    const selectedByType = useMemo(() => {
        const map = {};
        Object.entries(selectedFilters || {}).forEach(([type, value]) => {
            const vals = Array.isArray(value) ? value : [value];
            map[type] = new Set(vals);
        });
        return map;
    }, [selectedFilters]);

    const handleSearchChange = (stateKey, value) => {
        setSearchQueries(prev => ({...prev, [stateKey]: value}));
        setExpandedFilters(prev => ({...prev, [stateKey]: false}));
    };

    const toggleShowMore = (stateKey) => {
        setExpandedFilters(prev => ({...prev, [stateKey]: !prev[stateKey]}));
    };

    const handleCloseButtonClick = () => {
        setIsFilterOverlayVisible(false);
    };

    const renderFilterContent = (filter, stateKey) => {
        const {displayedOptionsLimit, showSearchField, searchFieldPlaceholder} = filter;

        const rawQuery = searchQueries[stateKey];
        const expanded = !!expandedFilters[stateKey];

        const {visibleOptions, hasMoreThanLimit} = getVisibleOptionsData(filter, rawQuery, expanded);
        const showMoreButton = filter.options.length > displayedOptionsLimit;

        return (
            <div className="accordion--filter-section">
                {showSearchField && (
                    <div className="filter-search-field">
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none">
                            <path stroke="#000" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                  d="m23 22.267-7.057-6.983m2.264-5.762c0 4.707-3.852 8.522-8.604 8.522C4.852 18.044 1 14.23 1 9.522 1 4.815 4.852 1 9.603 1c4.752 0 8.604 3.815 8.604 8.522Z"/>
                        </svg>
                        <input
                            type="text"
                            value={rawQuery || ''}
                            placeholder={
                                searchFieldPlaceholder ??
                                (translate('accommodation-overview.filter.searchPlaceholder') || 'Search')
                            }
                            onChange={(e) => handleSearchChange(stateKey, e.target.value)}
                        />
                    </div>
                )}

                {visibleOptions.map((option) => {
                    const isGroup = option.isGroup;
                    const groupSelected = isGroup && isGroupSelected(option, selectedByType);
                    const coveredByGroup = isOptionCoveredBySelectedGroup(option, filter, selectedByType);

                    const isChecked = isGroup
                        ? (!coveredByGroup && groupSelected)
                        : (!coveredByGroup && !!selectedByType[option.type]?.has(option.id));

                    return (
                        <div className="checkbox" key={`${filter.id}-${option.id}`}>
                            <label>
                                {isGroup ? (
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleFilterGroupChange(option.items, !isChecked)}
                                    />
                                ) : (
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleFilterChange(option.type, option.id, !isChecked)}
                                    />
                                )}
                                <span></span>
                                {option.theme && (
                                    <i className={clsx('world', 'theme--' + option.theme)}></i>
                                )}
                                <div>{option.label}</div>
                            </label>
                        </div>
                    );
                })}

                {showMoreButton && hasMoreThanLimit && (
                    <button
                        className="show-more-button"
                        type="button"
                        onClick={() => toggleShowMore(stateKey)}
                    >
                        {expanded
                            ? (translate('accommodation-overview.filter.showLess') || 'Show less')
                            : (translate('accommodation-overview.filter.showMore') || 'Show more')}
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className="accordion--inner">
            <div className="accordion--header-mobile">
                <div className="accordion--header-mobile--title">
                    Filter <label className="overlay-close" onClick={handleCloseButtonClick}></label>
                </div>
                {isMobile && hasActiveFilters && (
                    <div className="accommodation-overview--filter-pills">
                        <div className="pill-container">
                            <ActiveFilters/>
                        </div>
                    </div>
                )}
            </div>

            {isMobile && <SearchField/>}

            {Object.values(subFilters).map((filter) => {
                const stateKey = `sub:${filter.id}`;
                return (
                    <Accordion
                        key={stateKey}
                        title={filter.label}
                        filterCount={getFilterCount(selectedByType, filter)}
                        open={filter.isOpen}
                        className="is-highlighted"
                    >
                        {renderFilterContent(filter, stateKey)}
                    </Accordion>
                );
            })}

            {hasResultsWithPrices && (
                <Accordion
                    key={'filter-price-range'}
                    title={translate('accommodation-overview.filter.priceRange.label')}
                    open={true}
                >
                    <div className={'accordion--filter-section'}>
                        <PriceRange onChange={handlePriceRangeChange}/>
                    </div>
                </Accordion>
            )}

            {Object.values(filters.global).filter(filter => !excludedGlobalFilterIds.has(filter.id)).map((filter) => {
                const stateKey = `global:${filter.id}`;
                return (
                    <Accordion
                        key={stateKey}
                        title={filter.label}
                        filterCount={getFilterCount(selectedByType, filter)}
                        open={filter.isOpen}
                    >
                        {renderFilterContent(filter, stateKey)}
                    </Accordion>
                );
            })}

            <Accordion
                title={translate('accommodation-overview.filter.misc.label')}
                open={true}
                className="accordion--misc"
            >
                <div className="accordion--filter-section">
                    <Pets/>
                    {/*<AccessibleFacilities/>*/}
                </div>
            </Accordion>
        </div>
    );
};

export default Filters;
